import { io as defaultIo } from "socket.io-client";

export const ML_STATUS_EVENT = "telemed:ml-status";
export const ENTITLEMENT_EVENT = "account.entitlement.changed";
export const DEFAULT_SOCKET_PATH = "/telemed/socket.io";

export const createRealtimeClient = ({
  url,
  token,
  path = DEFAULT_SOCKET_PATH,
  ioFactory = defaultIo,
  onReconnect = () => {},
  onDisconnect = () => {},
  onError = () => {},
} = {}) => {
  let socket;
  let attached = false;
  let closed = false;
  let hasConnected = false;
  let initialConnectFailed = false;
  const subscribers = new Set();
  const entitlementSubscribers = new Set();

  const report = () => {
    try {
      Promise.resolve(onError(new Error("realtime callback failed"))).catch(
        () => {}
      );
    } catch (error) {
      // Error reporting must not affect transport cleanup.
    }
  };

  const invoke = (callback, ...args) => {
    try {
      Promise.resolve(callback(...args)).catch(report);
    } catch (error) {
      report();
    }
  };

  const handleConnect = () => {
    if (closed) return;
    if (hasConnected || initialConnectFailed) invoke(onReconnect);
    hasConnected = true;
    initialConnectFailed = false;
  };
  const handleDisconnect = (reason) => {
    if (closed) return;
    invoke(onDisconnect, reason);
  };
  const handleConnectError = () => {
    if (closed) return;
    if (!hasConnected) initialConnectFailed = true;
    report();
  };
  const handleUpdate = (event) => {
    if (closed) return;
    subscribers.forEach((listener) => invoke(listener, event));
  };
  const handleEntitlementUpdate = (event) => {
    if (closed) return;
    entitlementSubscribers.forEach((listener) => invoke(listener, event));
  };
  const registrations = [
    ["connect", handleConnect],
    ["disconnect", handleDisconnect],
    ["connect_error", handleConnectError],
    [ML_STATUS_EVENT, handleUpdate],
    [ENTITLEMENT_EVENT, handleEntitlementUpdate],
  ];

  const ensureSocket = () => {
    if (!socket) {
      socket = ioFactory(url, {
        path,
        auth: { token },
        autoConnect: false,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        randomizationFactor: 0.5,
      });
    }
    if (!attached) {
      registrations.forEach(([name, listener]) => socket.on(name, listener));
      attached = true;
    }
    return socket;
  };

  const subscribe = (listener) => {
    if (typeof listener !== "function" || closed) return;
    subscribers.add(listener);
    ensureSocket();
  };

  const unsubscribe = (listener) => {
    subscribers.delete(listener);
  };

  const subscribeEntitlement = (listener) => {
    if (typeof listener !== "function" || closed) return;
    entitlementSubscribers.add(listener);
    ensureSocket();
  };

  const unsubscribeEntitlement = (listener) => {
    entitlementSubscribers.delete(listener);
  };

  const connect = () => {
    if (closed) return;
    ensureSocket().connect();
  };

  const forceReconnect = () => {
    if (closed) return;
    const target = ensureSocket();
    if (target.connected) target.disconnect();
    target.connect();
  };

  const disconnect = () => {
    if (closed) return;
    closed = true;
    subscribers.clear();
    entitlementSubscribers.clear();
    if (!socket) return;
    if (attached) {
      registrations.forEach(([name, listener]) => socket.off(name, listener));
      attached = false;
    }
    socket.disconnect();
  };

  return {
    subscribe,
    unsubscribe,
    subscribeEntitlement,
    unsubscribeEntitlement,
    connect,
    forceReconnect,
    disconnect,
  };
};

export default createRealtimeClient;
